/**
 * Copy + classificação dos códigos de negação de acesso processual.
 *
 * Distinção central:
 *   - Negação DEFINITIVA (`isDefinitiveDenial`) — você não tem acesso e isso
 *     não muda sozinho. Persiste no `AccessOracle` (IDB) e desabilita o
 *     refetch da query. Ex.: não é parte do processo, tribunal não integrado.
 *   - Indisponibilidade TRANSITÓRIA — o sistema não conseguiu verificar agora.
 *     NÃO persiste: fica como `error` normal da query, retentável. Persistir
 *     manteria o usuário bloqueado mesmo depois de o serviço voltar.
 */

/** Códigos de negação definitiva — persistidos no AccessOracle e bloqueiam refetch. */
const DEFINITIVE_DENIAL_CODES = new Set<string>([
  'ACESSO_NEGADO_PRIVADO',
  'TRIBUNAL_NAO_INTEGRADO',
  'TRIBUNAL_NAO_INTEGRADO_SIGILOSO',
  'DOCUMENTO_ACESSO_NEGADO',
]);

export function isDefinitiveDenial(codigo: string | null | undefined): codigo is string {
  return !!codigo && DEFINITIVE_DENIAL_CODES.has(codigo);
}

export interface AccessErrorCopy {
  title: string;
  description: string;
  /** true quando faz sentido oferecer "Tentar novamente" (falha transitória). */
  retryable: boolean;
}

export function getAccessErrorCopy(codigo: string | null | undefined): AccessErrorCopy {
  switch (codigo) {
    case 'ACESSO_NEGADO_PRIVADO':
      return {
        title: 'Acesso negado',
        description:
          'Você não consta como parte deste processo. Se for parte e isso estiver incorreto, procure o tribunal para regularizar a habilitação dos autos.',
        retryable: false,
      };
    case 'TRIBUNAL_NAO_INTEGRADO_SIGILOSO':
      return {
        title: 'Tribunal não integrado',
        description:
          'Este processo é sigiloso e o tribunal de origem ainda não está integrado à base nacional do CNJ. Não é possível exibir os autos por aqui.',
        retryable: false,
      };
    case 'TRIBUNAL_NAO_INTEGRADO':
      return {
        title: 'Tribunal não integrado',
        description:
          'O tribunal de origem ainda não está integrado à base nacional do CNJ. Não é possível exibir os autos por aqui.',
        retryable: false,
      };
    case 'PROCESSO_NAO_LOCALIZADO':
      return {
        title: 'Processo não localizado',
        description:
          'Não localizamos este processo na base nacional do CNJ. Confira o número — processos muito recentes podem levar alguns minutos para aparecer, e tribunais ainda não integrados ao CNJ podem não estar disponíveis para consulta.',
        retryable: true,
      };
    case 'ACESSO_VALIDACAO_INDISPONIVEL':
      return {
        title: 'Não foi possível verificar o acesso',
        description:
          'O sistema não conseguiu confirmar seu acesso a este processo agora. Tente novamente em alguns instantes.',
        retryable: true,
      };
    default:
      return {
        title: 'Não foi possível acessar o processo',
        description: 'Tente novamente mais tarde ou consulte o tribunal de origem.',
        retryable: true,
      };
  }
}
